/**
 * HOW LONG SOMETHING HAS BEEN WAITING — and how alarmed the screen should look about it.
 *
 * The approvals hub has aged its queue on this scale since it was built; the transfers register had
 * no ageing at all, so a transfer dispatched in July and never received looked exactly like one
 * raised this morning. Rather than invent a second set of thresholds for a second screen — which is
 * how an app ends up calling four days "fine" in one place and "overdue" in another — the scale
 * moved here and both read it.
 *
 * The buckets are deliberately NOT colours. Tag and Pill do not share a palette (Tag has no orange),
 * and a shared helper that returned one component's tokens would quietly decide the other's design.
 * Each surface maps the bucket to its own vocabulary; only the thresholds are shared.
 */

export type AgeBucket = 'fresh' | 'day' | 'ageing' | 'stale';

/**
 * The one ageing scale: same day, a day or two, most of a week, over a week.
 *
 * A week is the point at which "somebody is getting to it" stops being a fair reading — every step
 * these buckets age (a dispatch, a goods receipt, settling a shortage) is a day's work at most.
 */
export const ageBucket = (days: number): AgeBucket =>
  days >= 7 ? 'stale' : days >= 3 ? 'ageing' : days >= 1 ? 'day' : 'fresh';

/**
 * Whole days from `at` until now, floored and never negative.
 *
 * Returns null for a missing or unparseable value so a caller can tell "nothing is waiting" from
 * "waiting zero days" — a transfer that is finished has no clock, and printing `today` on it would
 * say it is waiting when it is not.
 */
export function waitingDays(at: string | Date | null | undefined): number | null {
  if (at === null || at === undefined || at === '') return null;
  const t = new Date(at).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

/** "today" / "1 day" / "41 days". */
export const waitingLabel = (days: number): string =>
  days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'}`;
